import { BasicResponseDTO } from '@dto/basicResponse.dto';
import { API_URL } from 'src/data/Api';
import { getApi } from 'src/util/apiCalls';

// Local mirror of the BE DashboardTreeItem DTO (common/dashboard.dto.ts on the BE branch);
// switch to the @dto import once that branch is merged.
interface DashboardTreeItem {
  type: string;
  id: string;
  label: string;
  children?: DashboardTreeItem[];
}

interface DashboardTreeItemResponseDTO extends BasicResponseDTO {
  data: DashboardTreeItem[];
}

export type DashTreeNodeType = 'folder' | 'dashboard' | 'group' | 'user_group';

export interface DashTreeSourceNode {
  type: DashTreeNodeType;
  id: string;
  label: string;
  groupId?: number;
  groupRoot?: boolean;
  children?: DashTreeSourceNode[];
}

export function mapDashTree<T>(
  src: DashTreeSourceNode[],
  build: (n: DashTreeSourceNode, children: T[]) => T,
): T[] {
  return src.map(n => build(n, n.children ? mapDashTree(n.children, build) : []));
}

const typeRank = (t: DashTreeNodeType): number =>
  t === 'folder' ? 0 : t === 'dashboard' ? 1 : t === 'group' ? 2 : 3;

function sortNodes(nodes: DashTreeSourceNode[]): DashTreeSourceNode[] {
  nodes.sort((a, b) => typeRank(a.type) - typeRank(b.type) || a.label.localeCompare(b.label));
  nodes.forEach(n => { if (n.children) sortNodes(n.children); });
  return nodes;
}

function normalize(item: DashboardTreeItem, inheritedGroupId?: number): DashTreeSourceNode {
  const isUserGroup = item.type === 'user_group';
  const groupId = inheritedGroupId ?? (isUserGroup ? Number(item.id) : undefined);
  return {
    type: item.type as DashTreeNodeType,
    id: item.id,
    label: item.label,
    ...(groupId != null ? { groupId } : {}),
    ...(isUserGroup ? { groupRoot: true } : {}),
    ...(item.children ? { children: item.children.map(c => normalize(c, groupId)) } : {}),
  };
}

export async function loadDashboardTree(signal: AbortSignal): Promise<DashTreeSourceNode[]> {
  const resp = await getApi<DashboardTreeItemResponseDTO>(API_URL.DASHBOARD_TREE, signal);
  return sortNodes(resp.data.data.map(item => normalize(item)));
}
